// Auth hook — Firebase Auth edition (Phase 3).
//
// Replaces the Supabase-Auth version that shipped through Phase 2. The
// public API surface (user/signIn/signUp/signOut/resetPassword/updatePassword/
// updateProfile) is unchanged so the 6 auth pages don't need rewrites,
// they just keep calling useAuth() and the behavior maps to Firebase
// under the hood.
//
// Key Firebase ↔ Supabase translations:
//   - supabase.auth.getSession()        → auth.currentUser
//   - supabase.auth.onAuthStateChange   → onAuthStateChanged
//   - signInWithPassword                → signInWithEmailAndPassword
//   - signUp({...emailRedirectTo})      → createUser + sendEmailVerification
//   - signOut                           → firebase signOut
//   - resetPasswordForEmail             → sendPasswordResetEmail
//   - updateUser({password})            → updatePassword(currentUser, ...)
//   - updateUser({email})               → updateEmail / verifyBeforeUpdateEmail
//   - user_metadata.display_name        → user.displayName (top-level)
//
// Cross-tab sign-out: Firebase Auth's onAuthStateChanged fires across
// all open tabs when one signs out (it syncs via IndexedDB by default,
// same effect as Supabase's localStorage broadcast). The hard-redirect
// pattern from the Supabase version is preserved verbatim.

import {
  useState,
  useEffect,
  useCallback,
  useRef,
  createContext,
  useContext,
} from 'react';
import * as Sentry from '@sentry/react';
import { identifyUser, resetAnalytics } from '@/lib/analytics';
import { auth } from '@/lib/firebase';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  updatePassword as firebaseUpdatePassword,
  verifyBeforeUpdateEmail,
  updateProfile as firebaseUpdateProfile,
  EmailAuthProvider,
  reauthenticateWithCredential,
  deleteUser as firebaseDeleteUser,
  GoogleAuthProvider,
  signInWithPopup,
  reauthenticateWithPopup,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  type User as FirebaseUser,
  type ConfirmationResult,
} from 'firebase/auth';
import {
  deleteAccount as apiDeleteAccount,
  sendVerificationCode as apiSendVerificationCode,
  sendResetEmail as apiSendReset,
} from '@/lib/api';

interface AuthUser {
  id: string;
  email: string;
  displayName?: string;
  /** Firebase email_verified. Gates app access — AuthGate sends
   *  unverified users to /auth/verify-email, and the backend 403s them. */
  emailVerified: boolean;
  /** Sign-in providers linked to this account: 'password' | 'google.com' |
   *  'phone'. Drives provider-aware re-auth (e.g. Google users have no
   *  password, so account deletion re-auths via a Google popup instead). */
  providers: string[];
}

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error?: string }>;
  signUp: (email: string, password: string, displayName?: string) => Promise<{ error?: string; needsVerification?: boolean }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error?: string }>;
  updatePassword: (newPassword: string) => Promise<{ error?: string }>;
  updateProfile: (data: { displayName?: string; email?: string }) => Promise<{ error?: string }>;
  /** Gated email change — re-authenticates with the current password, then
   *  sends a confirmation link to the NEW address (verifyBeforeUpdateEmail).
   *  The email changes ONLY after the user clicks that link (which returns
   *  them to /auth/profile). Password accounts only. */
  changeEmail: (newEmail: string, currentPassword: string) => Promise<{ error?: string }>;
  /** Permanently delete the account + all data. Re-authenticates first:
   *  password users pass their password; Google users get a Google popup
   *  (no password needed). On success, the auth account is gone and
   *  onAuthStateChanged hard-redirects to sign-in. */
  deleteAccount: (password?: string) => Promise<{ error?: string }>;
  /** Sign in / sign up with Google (popup). Google users come back with a
   *  verified email, so they skip the verification gate. */
  signInWithGoogle: () => Promise<{ error?: string }>;
  /** Phone auth step 1 — send an SMS code to an E.164 number. Renders an
   *  invisible reCAPTCHA into `recaptchaContainerId`. */
  startPhoneSignIn: (phoneNumber: string, recaptchaContainerId: string) => Promise<{ error?: string }>;
  /** Phone auth step 2 — confirm the SMS code from startPhoneSignIn. */
  confirmPhoneCode: (code: string) => Promise<{ error?: string }>;
  /** Re-send the email-verification link to the current user. */
  resendVerification: () => Promise<{ error?: string }>;
  /** Reload the Firebase user + force a fresh ID token so a just-clicked
   *  verification link is reflected (the token's email_verified is a stale
   *  snapshot otherwise). Returns whether the email is now verified. */
  refreshVerification: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | null>(null);

// Firebase Auth errors come back as { code, message } where the code is
// the machine-readable string. Map the common ones to user-friendly
// messages — Firebase's default `message` is technical ("Firebase:
// Error (auth/wrong-password)") and unhelpful in the UI.
function friendlyAuthError(err: unknown): string {
  if (err && typeof err === 'object' && 'code' in err) {
    const code = String((err as { code: string }).code);
    switch (code) {
      case 'auth/invalid-email':
        return 'That email address looks invalid.';
      case 'auth/user-not-found':
      case 'auth/wrong-password':
      case 'auth/invalid-credential':
        return 'Wrong email or password.';
      case 'auth/email-already-in-use':
        return 'An account with that email already exists. Try signing in.';
      case 'auth/weak-password':
        return 'Password is too weak — use at least 6 characters.';
      case 'auth/too-many-requests':
        return "We've seen too many attempts on this account recently. Please wait a few minutes and try again.";
      case 'auth/network-request-failed':
        return "Network error. Check your connection and try again.";
      case 'auth/requires-recent-login':
        return 'For security, please sign out and sign in again before making this change.';
      case 'auth/user-disabled':
        return 'This account has been disabled. Contact support if you think this is a mistake.';
      case 'auth/popup-closed-by-user':
      case 'auth/cancelled-popup-request':
        return 'Sign-in was cancelled.';
      case 'auth/popup-blocked':
        return 'Your browser blocked the sign-in popup. Allow popups and try again.';
      case 'auth/account-exists-with-different-credential':
        return 'An account already exists with this email using a different sign-in method. Sign in that way instead.';
      case 'auth/invalid-phone-number':
      case 'auth/missing-phone-number':
        return 'That phone number looks invalid. Include the country code, e.g. +1 555 123 4567.';
      case 'auth/invalid-verification-code':
        return 'That verification code is incorrect.';
      case 'auth/code-expired':
        return 'That code expired — request a new one.';
      default:
        // Strip Firebase's "Firebase: " prefix and the trailing "(auth/...)"
        const msg = err && 'message' in err ? String((err as { message: string }).message) : '';
        return msg.replace(/^Firebase:\s*/, '').replace(/\s*\(auth\/[a-z-]+\)\.?$/, '');
    }
  }
  if (err instanceof Error) return err.message;
  return 'Something went wrong. Please try again.';
}

function firebaseUserToAppUser(fbUser: FirebaseUser): AuthUser {
  return {
    id: fbUser.uid,
    email: fbUser.email || '',
    displayName: fbUser.displayName || '',
    emailVerified: fbUser.emailVerified,
    providers: (fbUser.providerData || []).map((p) => p.providerId),
  };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  // Track previous signed-in state so we can detect the SIGNED_OUT
  // transition for the hard-redirect cross-tab cleanup. Firebase's
  // onAuthStateChanged doesn't ship event types like Supabase does —
  // we synthesize the "just-signed-out" signal from null transitions.
  const wasSignedInRef = useRef<boolean>(false);
  // Phone-auth handles: the invisible reCAPTCHA verifier + the pending
  // confirmation between "send code" and "confirm code".
  const recaptchaRef = useRef<RecaptchaVerifier | null>(null);
  const phoneConfirmationRef = useRef<ConfirmationResult | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (fbUser) => {
      if (fbUser) {
        setUser(firebaseUserToAppUser(fbUser));
        Sentry.setUser({
          id: fbUser.uid,
          email: fbUser.email || undefined,
        });
        // Tie PostHog events to this user so onboarding funnels are per-person.
        identifyUser(fbUser.uid, { email: fbUser.email || undefined });
        wasSignedInRef.current = true;
      } else {
        setUser(null);
        Sentry.setUser(null);
        resetAnalytics();
        // Hard-redirect on sign-out (cross-tab). Mirrors the Supabase
        // version's behavior: wipes in-memory caches (useCredits balance,
        // wizard productFiles, etc.), cancels in-flight /api/* requests
        // carrying the now-stale ID token, and dodges any stale-render
        // race. Only triggers on signed-in → signed-out; skip during
        // initial cold-boot (wasSignedInRef starts false) and when
        // already on an auth page.
        if (wasSignedInRef.current) {
          try {
            sessionStorage.removeItem('sp_wizard_snapshot_v1');
          } catch {
            // ignore
          }
          const onAuthPage = window.location.pathname.startsWith('/auth/');
          if (!onAuthPage) {
            window.location.replace('/auth/sign-in');
            return; // skip the setLoading below, we're navigating away
          }
        }
        wasSignedInRef.current = false;
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
      return {};
    } catch (err) {
      return { error: friendlyAuthError(err) };
    }
  }, []);

  const signUp = useCallback(async (email: string, password: string, displayName?: string) => {
    try {
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      // Stamp display name onto the Firebase user record so it's available
      // on subsequent sessions (Firebase doesn't accept displayName as a
      // createUser param; it's a post-create update).
      if (displayName && cred.user) {
        try {
          await firebaseUpdateProfile(cred.user, { displayName });
        } catch {
          // Non-fatal — sign-up succeeded; profile field can be set later.
        }
      }
      // Don't send here — the verify page auto-sends the 6-digit code on mount
      // (single send point, works whether the user got here via fresh signup or
      // an AuthGate bounce). cred.user is now signed in (email_verified=false);
      // the verify page's authed send-code call is exempted from the gate.
      return { needsVerification: true };
    } catch (err) {
      return { error: friendlyAuthError(err) };
    }
  }, []);

  const signOut = useCallback(async () => {
    await firebaseSignOut(auth);
    setUser(null);
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    // Branded reset email via our backend (Resend). Explicit UX (product
    // choice): tell the user when no account exists rather than the neutral
    // "if an account exists…" message — the backend returns user_not_found.
    const { ok, reason } = await apiSendReset(email, window.location.origin);
    if (ok) return {};
    if (reason === 'user_not_found') {
      return { error: 'There is no account registered with this email.' };
    }
    if (reason === 'rate_limited') {
      return { error: "We've sent too many reset emails to this address recently. Please wait a few minutes and try again." };
    }
    return { error: 'Could not send the reset email. Please try again.' };
  }, []);

  const updatePassword = useCallback(async (newPassword: string) => {
    if (!auth.currentUser) {
      return { error: 'You must be signed in to change your password.' };
    }
    try {
      await firebaseUpdatePassword(auth.currentUser, newPassword);
      return {};
    } catch (err) {
      return { error: friendlyAuthError(err) };
    }
  }, []);

  const changeEmail = useCallback(async (newEmail: string, currentPassword: string) => {
    const cu = auth.currentUser;
    if (!cu || !cu.email) {
      return { error: 'You must be signed in to change your email.' };
    }
    try {
      // Re-authenticate with the current password. Firebase requires a recent
      // login for email changes; doing it explicitly gives a clean error path
      // (wrong password) instead of the generic auth/requires-recent-login.
      const cred = EmailAuthProvider.credential(cu.email, currentPassword);
      await reauthenticateWithCredential(cu, cred);
      // Send the confirmation link to the NEW address. The email switches only
      // after the user clicks it; the link returns them to /auth/profile.
      await verifyBeforeUpdateEmail(cu, newEmail, {
        url: `${window.location.origin}/auth/profile`,
      });
      return {};
    } catch (err) {
      return { error: friendlyAuthError(err) };
    }
  }, []);

  const updateProfile = useCallback(async (data: { displayName?: string; email?: string }) => {
    if (!auth.currentUser) {
      return { error: 'You must be signed in.' };
    }
    try {
      if (data.displayName !== undefined) {
        await firebaseUpdateProfile(auth.currentUser, { displayName: data.displayName });
      }
      if (data.email !== undefined && data.email !== auth.currentUser.email) {
        // verifyBeforeUpdateEmail sends a verification email to the new
        // address; the change applies only after the user clicks the
        // link. This is Firebase's safe path — updateEmail (the
        // legacy direct path) is deprecated for new projects.
        await verifyBeforeUpdateEmail(auth.currentUser, data.email, {
          url: `${window.location.origin}/auth/profile`,
        });
      }
      // Refresh local state to reflect displayName change immediately.
      if (auth.currentUser) {
        setUser(firebaseUserToAppUser(auth.currentUser));
      }
      return {};
    } catch (err) {
      return { error: friendlyAuthError(err) };
    }
  }, []);

  const signInWithGoogle = useCallback(async () => {
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });
      await signInWithPopup(auth, provider);
      // onAuthStateChanged updates `user`; the sign-in page redirects.
      return {};
    } catch (err) {
      return { error: friendlyAuthError(err) };
    }
  }, []);

  const startPhoneSignIn = useCallback(async (phoneNumber: string, recaptchaContainerId: string) => {
    try {
      // Fresh verifier each attempt — reusing a cleared one throws
      // "reCAPTCHA has already been rendered".
      if (recaptchaRef.current) {
        try { recaptchaRef.current.clear(); } catch { /* already gone */ }
        recaptchaRef.current = null;
      }
      recaptchaRef.current = new RecaptchaVerifier(auth, recaptchaContainerId, {
        size: 'invisible',
      });
      phoneConfirmationRef.current = await signInWithPhoneNumber(
        auth, phoneNumber.trim(), recaptchaRef.current,
      );
      return {};
    } catch (err) {
      // Reset the verifier so the next attempt starts clean.
      if (recaptchaRef.current) {
        try { recaptchaRef.current.clear(); } catch { /* ignore */ }
        recaptchaRef.current = null;
      }
      return { error: friendlyAuthError(err) };
    }
  }, []);

  const confirmPhoneCode = useCallback(async (code: string) => {
    if (!phoneConfirmationRef.current) {
      return { error: 'Request a code first.' };
    }
    try {
      await phoneConfirmationRef.current.confirm(code.trim());
      phoneConfirmationRef.current = null;
      // onAuthStateChanged updates `user`; the sign-in page redirects.
      return {};
    } catch (err) {
      return { error: friendlyAuthError(err) };
    }
  }, []);

  const resendVerification = useCallback(async () => {
    if (!auth.currentUser) return { error: 'You must be signed in.' };
    // Branded 6-digit verification CODE via our backend (Resend). OTP flow —
    // no magic link, so no second tab / no verify loop.
    const { ok } = await apiSendVerificationCode();
    return ok ? {} : { error: 'Could not send the verification code. Please try again.' };
  }, []);

  const refreshVerification = useCallback(async () => {
    if (!auth.currentUser) return false;
    try {
      await auth.currentUser.reload();
      // Force a fresh ID token so the backend sees the updated
      // email_verified claim (the cached token still says false otherwise).
      await auth.currentUser.getIdToken(true);
      setUser(firebaseUserToAppUser(auth.currentUser));
      return auth.currentUser.emailVerified;
    } catch {
      return false;
    }
  }, []);

  const deleteAccount = useCallback(async (password?: string) => {
    const u = auth.currentUser;
    if (!u) {
      return { error: 'You must be signed in.' };
    }
    const providers = (u.providerData || []).map((p) => p.providerId);
    // 1. Re-authenticate (provider-aware). This refreshes the token so both
    //    the backend wipe and the Firebase deleteUser below succeed —
    //    deleteUser otherwise throws auth/requires-recent-login.
    try {
      if (providers.includes('password') && u.email) {
        // Email/password account → confirm with the password.
        if (!password) return { error: 'Enter your password to confirm.' };
        const cred = EmailAuthProvider.credential(u.email, password);
        await reauthenticateWithCredential(u, cred);
      } else if (providers.includes('google.com')) {
        // Google account has no password → re-confirm via a Google popup.
        await reauthenticateWithPopup(u, new GoogleAuthProvider());
      }
      // Other providers (e.g. phone) fall through — deleteUser will surface
      // requires-recent-login if the token is too old.
    } catch (err) {
      return { error: friendlyAuthError(err) };
    }
    // 2. Wipe all app data (Cloud SQL cascade + GCS) on the backend.
    try {
      await apiDeleteAccount();
    } catch (err) {
      return { error: err instanceof Error ? err.message : 'Account deletion failed.' };
    }
    // 3. Remove the Firebase auth account. onAuthStateChanged fires
    //    SIGNED_OUT → the cross-tab hard-redirect handles navigation.
    //    Data is already gone; if the auth shell can't be removed, just
    //    sign out so the session ends.
    try {
      await firebaseDeleteUser(u);
    } catch {
      try { await firebaseSignOut(auth); } catch { /* ignore */ }
    }
    return {};
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signUp, signOut, resetPassword, updatePassword, updateProfile, changeEmail, deleteAccount, resendVerification, refreshVerification, signInWithGoogle, startPhoneSignIn, confirmPhoneCode }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
